import { NestedFilterItem } from '@apeiron/common'
import {
  PlanetFilterInput,
  PlanetSkill,
  PlanetSkillFilterInput,
  PlanetSkillType,
  SkillFilterMode
} from '@apeiron/planet'
import { NextImage, noForwardCustomProps } from '@apeiron/temp'
import { styled } from '@mui/material'
import PlanetSkillClassFilter from '@src/components/planet/filter/skillFilter/PlanetSkillClassFilter'
import PlanetSkillElementFilter from '@src/components/planet/filter/skillFilter/PlanetSkillElementFilter'
import PlanetSkillItem from '@src/components/planet/share/planetSkill/PlanetSkillItem'
import NestedFilter from '@src/components/share/filter/nestedFilter/NestedFilter'
import NestedFilterWrapper from '@src/components/share/filter/nestedFilter/NestedFilterWrapper'
import SkillDescriptionTab from '@src/components/share/skill/SkillDescriptionTab'
import { usePlanetClassContext } from '@src/contexts/planet/PlanetClassContext'
import { usePlanetSkillsContext } from '@src/contexts/planet/PlanetSkillsContext'
import * as R from 'ramda'
import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

const StyledPlanetSkillItem = styled(
  PlanetSkillItem,
  noForwardCustomProps(['popup'])
)<SkillFilterItemProps>`
  ${props => (props.popup ? '' : 'margin: 0px 8px;')}
  .Custom-Image-Box {
    .Custom-Image {
      width: 70px;
    }
  }

  .Custom-Info-Box {
    padding-top: 10px;
    color: #333333;
  }

  .Custom-Element-Name {
    font-size: 16px;
    font-weight: 400;

    .Mui-focused & {
      color: var(
        --invert-color
      ); // it is set in PlanetSkillItem ElementName component
    }

    li:hover & {
      color: var(--invert-color);
    }
  }

  .Custom-Name-Box {
    .Custom-Name {
      color: #333333;
      font-size: 16px;
      font-weight: 400;
    }
    .Custom-Skill-Playback {
      .Custom-Icon {
        color: black;
        font-size: 24px;
      }
    }
  }

  .Custom-Description {
    color: #333333;
    font-weight: 400;
    margin-top: 6px;
  }

  .Custom-Upgrade {
    .Custom-Button-Tab {
      gap: 3px;
      .Custom-Button {
        height: 24px;
        padding: 0px 3px;
        p {
          font-weight: 500;
        }
      }
    }

    .Custom-Description {
      color: #333333;
    }
  }
`

const PlanetClassIcon = styled(NextImage)`
  width: 23px;
  aspect-ratio: 1;
  background-color: #ffffff11;
`

const DEFAULT_FILTER_INPUT: PlanetSkillFilterInput = {
  elements: [],
  planetClasses: []
}

const PlanetSkillFilter: FC<Props> = (props: Props) => {
  const { className, filterOptions, mode, onFilterOptionsChange } = props

  const { t } = useTranslation()

  const { activeSkills, passiveSkills, searchSkills, sortSkills } =
    usePlanetSkillsContext()

  const [selectedSkills, setSelectedSkills] = useState<NestedFilterItem[]>([])

  const { extractPlanetClass } = usePlanetClassContext()

  const activeMode = mode === SkillFilterMode.Active

  const allSkills = activeMode ? activeSkills : passiveSkills

  const field = activeMode ? 'cSkillID' : 'pSkillID'

  const title = activeMode
    ? t('filter.sections.skill.card')
    : t('filter.sections.skill.perk')

  const type = activeMode ? PlanetSkillType.Active : PlanetSkillType.Passive

  const handleOnFilterInputChange = useCallback(
    (input: PlanetFilterInput) => {
      const typedInput = input

      onFilterOptionsChange(typedInput)
    },
    [onFilterOptionsChange]
  )

  const handleOnRenderItem = useCallback(
    (item: NestedFilterItem, popup: boolean) => {
      const skill = item as PlanetSkill

      return <StyledPlanetSkillItem mode={mode} popup={popup} skill={skill} />
    },
    [mode]
  )

  const handleOnRenderPopupFilter = useCallback(
    (
      input: PlanetSkillFilterInput,
      onFilterItem: (filterInput: PlanetSkillFilterInput) => void
    ) => {
      return (
        <>
          <PlanetSkillElementFilter
            filterInput={input}
            onFilterChange={onFilterItem}
          />
          {mode === SkillFilterMode.Active && (
            <PlanetSkillClassFilter
              filterInput={input}
              onFilterChange={onFilterItem}
            />
          )}
        </>
      )
    },
    [mode]
  )

  const handleOnSearchByText = useCallback((item: NestedFilterItem) => {
    const typedItem = item as PlanetSkill

    return typedItem.name
  }, [])

  const handleOnSearchItems = useCallback(
    (input: PlanetFilterInput) => {
      const typedInput = input

      const uniqueKeys = R.pipe(
        R.propOr([], field),
        R.map(value => R.toString(value))
      )(typedInput)

      return !R.isEmpty(uniqueKeys)
        ? searchSkills({
            type,
            [field]: uniqueKeys
          })
        : []
    },
    [field, searchSkills, type]
  )

  const handleOnSearchInnerItems = useCallback(
    (input: PlanetSkillFilterInput) => {
      return searchSkills({
        type,
        elements: input.elements,
        planetClasses: input.planetClasses
      })
    },
    [searchSkills, type]
  )

  const handleOnSortSkills = useCallback(
    (items: NestedFilterItem[]) => {
      return sortSkills(items as PlanetSkill[])
    },
    [sortSkills]
  )

  const handleOnRenderActionItems = (item: NestedFilterItem) => {
    const typedItem = item as PlanetSkill

    const planetClass = extractPlanetClass(typedItem.attributes.planetClass)

    return planetClass ? <PlanetClassIcon src={planetClass.image} /> : <></>
  }

  const handleOnReset = useCallback(() => {
    const newFilter = {
      ...filterOptions,
      [field]: undefined
    }

    setSelectedSkills([])
    onFilterOptionsChange(newFilter)
  }, [field, filterOptions, onFilterOptionsChange])

  return (
    <NestedFilterWrapper
      className={className}
      title={title}
      fields={[field]}
      filterInput={filterOptions}
      handleOnReset={handleOnReset}
    >
      <NestedFilter
        title={title}
        allItems={allSkills}
        className={className}
        defaultSearchParam={DEFAULT_FILTER_INPUT}
        field={field}
        filterInput={filterOptions}
        additionalComponentInPopup={<SkillDescriptionTab />}
        searchPlaceholder={t('filter.sections.skill.placeholder')}
        onFilterInputChange={handleOnFilterInputChange}
        renderItem={handleOnRenderItem}
        renderPopupFilter={handleOnRenderPopupFilter}
        renderActionItems={handleOnRenderActionItems}
        searchByText={handleOnSearchByText}
        searchItems={handleOnSearchItems}
        searchInnerItems={handleOnSearchInnerItems}
        sortItems={handleOnSortSkills}
        selectedItems={selectedSkills}
        setSelectedItems={setSelectedSkills}
      />
    </NestedFilterWrapper>
  )
}

type SkillFilterItemProps = {
  popup: boolean
}

type Props = {
  className?: string
  mode: SkillFilterMode
  filterOptions: PlanetFilterInput
  onFilterOptionsChange: (input: PlanetFilterInput) => void
}

export default PlanetSkillFilter
